import { createClient as createContentfulClient, EntryFields, AssetFile } from 'contentful'

import type {
  ContentfulBlockGeneralItem,
  ContentfulBlockGeneralItemWithItems,
  ContentfulCompositeCollection,
  ContentfulProductOptionsRelationshipWrapper,
  ContentfulPageTypeKeys,
  ContentfulPage,
  ContentfulProductMeta,
  ContentfulPageMeta,
  ContentfulBlockHubSpotSnippet,
  ContentfulMicrocopy,
  ContentfulRedirectItem,
  ContentfulPunchoutUnitFields,
  ContentfulPageSkeleton,
  ContentfulProductOptionsRelationshipWrapperFields,
  Page,
} from '@/types'

import app from '@/app'
import settings from '@/settings'
import { PAGETYPES, EDUCATIONAL_CONTENT_TYPES } from '@/settings/constants'

type ContentfulSettingsField = {
  catalogs: ContentfulPage[]
  landingPages: ContentfulPage[]
  homepagePageMeta?: ContentfulPageMeta | null
  hubSpotGlobalTracker?: ContentfulBlockHubSpotSnippet | null
  microcopy?: ContentfulMicrocopy[]
  pgpConfigurationRelationship: ContentfulCompositeCollection<ContentfulProductOptionsRelationshipWrapper> | null
  productDefaultImage?: EntryFields.AssetLink
  redirects: ContentfulRedirectItem[]
  siteCopy: ContentfulCompositeCollection<
    ContentfulBlockGeneralItemWithItems<ContentfulBlockGeneralItem>
  >
}

type DataField = {
  productsMeta: ContentfulProductMeta[]
  slots: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
  punchout: ContentfulPunchoutUnitFields | object
  catalogs: Page[]
  allPageEntries: Page[]
  textCopyEntries: Record<string, Record<string, EntryFields.RichText | undefined>>
  landingPages: Page[]
  redirects: Record<string, string>
  homepagePageMeta?: ContentfulPageMeta | null
  hubSpotGlobalTracker?: ContentfulBlockHubSpotSnippet | null
  microcopy?: ContentfulMicrocopy[] | null
  pgpConfigurationRelationship: ContentfulProductOptionsRelationshipWrapperFields[]
  productDefaultImage?: EntryFields.AssetLink | null
}

const contentfulServiceDataField: DataField = {
  catalogs: [],
  allPageEntries: [],
  landingPages: [],
  redirects: {},
  productsMeta: [],
  punchout: {},
  slots: {},
  textCopyEntries: {},
  homepagePageMeta: null,
  hubSpotGlobalTracker: null,
  microcopy: null,
  pgpConfigurationRelationship: [],
  productDefaultImage: null,
}

const contentfulService = {
  initDefaultClient() {
    contentfulService.isPreviewMode = false
    contentfulService.client = createContentfulClient({
      space: settings.services.contentful.space.id,
      environment: settings.services.contentful.space.environment,
      accessToken: settings.services.contentful.accessToken,
      host: settings.services.contentful.host,
    })
  },
  initPreviewClient(previewAccessToken: string) {
    contentfulService.isPreviewMode = true
    contentfulService.client = createContentfulClient({
      space: settings.services.contentful.space.id,
      environment: settings.services.contentful.space.environment,
      accessToken: previewAccessToken,
      host: settings.services.contentful.previewHost,
    })
  },
  isPreviewMode: false,
  client: createContentfulClient({
    space: settings.services.contentful.space.id,
    environment: settings.services.contentful.space.environment,
    accessToken: settings.services.contentful.accessToken,
    host: settings.services.contentful.host,
  }),
  service: {
    data: contentfulServiceDataField,
    getPGPConfigurationFieldsMapping() {
      return contentfulService.service.data.pgpConfigurationRelationship
    },
    getSiteCopy(copyGroupId: string, copyItemId: string) {
      if (!contentfulService.service.data.textCopyEntries) return null
      return contentfulService.service.data.textCopyEntries[copyGroupId]?.[copyItemId]
    },
    getDataForComponent(slotName: string) {
      return contentfulService.service.data.slots[slotName]
    },
    getProductMetaBySlug(slug: string) {
      return contentfulService.service.data.productsMeta.find((el) => el.fields?.slug === slug)
    },
    getPagesByPageType(pageType: ContentfulPageTypeKeys) {
      let cPageCollection: Page[] = []
      const parseCatalogs = (arr: Page[]) => {
        cPageCollection = [
          ...cPageCollection,
          ...arr.filter((el) => el.fields?.pageType === pageType),
        ]
        arr.forEach((el) => {
          if (el.fields.subpages) {
            parseCatalogs(el.fields.subpages)
          }
          if (el.fields.subpagesGrouped) {
            parseCatalogs(el.fields.subpagesGrouped)
          }
        })
      }
      parseCatalogs(contentfulService.service.data.catalogs)
      return cPageCollection
    },
    getPageBySlug(slug: string) {
      let cPage: Page | undefined
      const parseCatalogs = (arr: Page[]) => {
        if (!cPage) {
          cPage = arr.find((el) => el.fields?.slug === slug)
          if (!cPage) {
            arr.forEach((el) => {
              if (el.fields?.subpages) {
                parseCatalogs(el.fields.subpages)
              }
              if (el.fields?.subpagesGrouped) {
                parseCatalogs(el.fields.subpagesGrouped)
              }
            })
          }
        }
      }
      parseCatalogs(contentfulService.service.data.catalogs)
      if (!cPage)
        cPage = contentfulService.service.data.landingPages.find((page) => page.slug === slug)
      if (contentfulService.isPreviewMode && !cPage)
        parseCatalogs(contentfulService.service.data.allPageEntries)
      return cPage
    },
    getProductDefaultImage(): AssetFile | string {
      return contentfulService.service.data.productDefaultImage?.fields?.file?.url || ''
    },
    getResourceDefaultImage(slug: string): string {
      return (
        contentfulService.service
          .getPageBySlug(slug)
          ?.fields?.includes?.find((el) => el.sys.contentType?.sys.id === 'resourceDefaultImage')
          ?.fields?.image.fields?.file.url ||
        contentfulService.service.data.productDefaultImage?.fields?.file?.url ||
        ''
      )
    },
    getHubspotGlobalTrackerSnippet() {
      return contentfulService.service.data.hubSpotGlobalTracker?.fields?.snippet
    },
    getPunchout() {
      return contentfulService.service.data.punchout || {}
    },
    getEducationalContentPage() {
      return contentfulService.service
        .getPagesByPageType(PAGETYPES.CATEGORY_RESOURCE_EDUCATIONAL_CONTENT)
        .pop()
    },
    getBlogsPage() {
      return contentfulService.service.getPagesByPageType(PAGETYPES.CATEGORY_RESOURCE_BLOGS).pop()
    },
    transformSlugToLink(slug = '') {
      return slug.includes('http') ? slug : `/${slug}`
    },
    isEducationalContent(pageType: string) {
      return EDUCATIONAL_CONTENT_TYPES.some((el) => el === pageType)
    },
    extractEntryType(entry: { sys?: { contentType?: { sys?: { id?: string } } } }) {
      return entry?.sys?.contentType?.sys?.id
    },
    getImageUrl(
      imageField: EntryFields.AssetLink | undefined,
      quality = 80,
      width: string | number = '',
      height: string | number = '',
    ) {
      if (!imageField?.fields?.file?.url) return ''
      return `${imageField.fields.file.url}?q=${quality}&w=${width}&h=${height}&fm=webp`
    },
    getImageAlt(imageField: EntryFields.AssetLink | undefined, imageTitle = '') {
      if (!imageField?.fields?.description) return imageTitle
      return imageField.fields.description
    },
    getExpertPage(slug: string) {
      const expertsPageEntry = contentfulService.service.getDataForComponent('expertsPage')
      return expertsPageEntry
        ? expertsPageEntry.fields?.includes?.find((el: Page) => el?.fields?.slug === slug)
        : null
    },
    getRedirect(from = '') {
      const redirectFrom = from.startsWith('//') ? `https:${from}` : from
      return contentfulService.service.data.redirects?.[redirectFrom] || ''
    },
    async initData({
      productsMeta,
      settingsEntries,
      slots,
      punchout,
      allPageEntries,
    }: {
      productsMeta: ContentfulProductMeta[]
      settingsEntries: ContentfulSettingsField
      slots: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
      punchout: ContentfulPunchoutUnitFields | object
      allPageEntries: ContentfulPage[]
    }) {
      const educationalContentItems: Page[] = []
      const decoratePages = (arr: ContentfulPage[], parentEl?: Page, currentIter = 1) => {
        if (currentIter === 6) return []
        currentIter++
        const decoratedPages: Page[] = arr
          .filter((el) => el.fields)
          .map((el) => {
            const decoratedPage: Page = {
              fields: {
                ...el.fields,
                subpages: [],
                subpagesGrouped: [],
              },
              crumbs: [],
              link: '',
              slug: '',
              pageTitleAsFacet: '',
              sys: el.sys,
            }
            if (!decoratedPage.crumbs.length) decoratedPage.crumbs = [decoratedPage]
            if (!decoratedPage.link) {
              if (contentfulService.service.isEducationalContent(el.fields.pageType)) {
                educationalContentItems.push(decoratedPage)
              } else {
                decoratedPage.link = contentfulService.service.transformSlugToLink(el.fields.slug)
                decoratedPage.slug = el.fields.slug
              }
            }
            const pageTitleAsFacet = el.fields.title?.trim()
            decoratedPage.pageTitleAsFacet = pageTitleAsFacet
            if (parentEl) {
              decoratedPage.parent = parentEl
              decoratedPage.crumbs = [...parentEl.crumbs, ...decoratedPage.crumbs]
              if (
                parentEl.fields.slug !== 'products' &&
                parentEl.fields.slug !== 'disease-focus' &&
                parentEl.fields.slug !== 'species' &&
                parentEl.fields.slug !== 'applications' &&
                parentEl.fields.slug !== 'industry'
              ) {
                decoratedPage.pageTitleAsFacet = `${parentEl.pageTitleAsFacet} > ${pageTitleAsFacet}`
                if (parentEl.link) {
                  decoratedPage.link = `${parentEl.link}${decoratedPage.link}`
                  decoratedPage.slug = el.fields.slug
                }
              }
            }
            if (el.fields.subpages) {
              decoratedPage.fields.subpages = decoratePages(
                el.fields.subpages,
                decoratedPage,
                currentIter,
              )
            }
            if (el.fields.subpagesGrouped) {
              decoratedPage.fields.subpagesGrouped = decoratePages(
                el.fields.subpagesGrouped,
                decoratedPage,
                currentIter,
              )
            }
            return decoratedPage
          })
        return decoratedPages
      }
      const initEducationalContent = (arr: Page[]) => {
        arr.forEach((el) => {
          const educationalContentPage = contentfulService.service.getEducationalContentPage()
          if (!educationalContentPage) return
          el.link = `${educationalContentPage.link}?facets=(resourceGroup:"${encodeURIComponent(
            el.fields.title,
          )}")`
        })
      }
      const initRichTextLinkInterceptor = () => {
        document.addEventListener('click', (e: MouseEvent) => {
          if (
            !e.target ||
            !(e.target instanceof HTMLElement) ||
            e.target.tagName.toLowerCase() !== 'a'
          )
            return
          const isRichTextContainsClickedElement = Array.from(
            document.querySelectorAll('.rich-text-wrapper'),
          ).some((el) => el.contains(e.target as Node))
          if (!isRichTextContainsClickedElement) return

          const target = e.target as HTMLAnchorElement
          const redirectUrl = contentfulService.service.getRedirect(target.href)
          if (redirectUrl) {
            window.open(redirectUrl, '_self')
            return
          }
          if (
            ['www.bioivt.com', 'bioivt.com', window.location.hostname].includes(target.hostname)
          ) {
            e.stopPropagation()
            e.preventDefault()
            app.config.globalProperties.$emitter.emit<'contentful.rich:bio-link'>(
              'contentful.rich:bio-link',
              target,
            )
          }
        })
      }
      const initSiteCopyData = (settingsEntries: ContentfulSettingsField) => {
        const siteCopyField = settingsEntries.siteCopy
        const transformedSiteCopyField: Record<
          string,
          Record<string, EntryFields.RichText | undefined>
        > = {}
        const rawCopyGroups = siteCopyField?.fields?.items
        if (!rawCopyGroups?.length) {
          return transformedSiteCopyField
        }

        const transformedCopyGroups = rawCopyGroups
          .filter((copyGroupItem) => {
            const entryType = copyGroupItem.sys.contentType?.sys.id
            return (
              entryType === 'blockGeneralItem' ||
              copyGroupItem.fields?.id ||
              copyGroupItem.fields?.items?.length
            )
          })
          .map((copyGroupItem) => {
            const copyItems =
              copyGroupItem.fields.items
                ?.filter((copyItem) => {
                  const entryType = copyItem.sys.contentType?.sys.id
                  return (
                    entryType === 'blockGeneralItem' &&
                    copyItem.fields?.id &&
                    copyItem.fields?.paragraphRich
                  )
                })
                .map((copyItem) => ({
                  copyItemId: copyItem.fields?.id,
                  copyItemValue: copyItem.fields?.paragraphRich,
                })) || []
            return {
              groupId: copyGroupItem.fields?.id,
              copyItems,
            }
          })
          .filter((copyGroupItem) => copyGroupItem.copyItems.length)

        transformedCopyGroups.forEach((copyGroupItem) => {
          transformedSiteCopyField[copyGroupItem.groupId] = copyGroupItem.copyItems.reduce<
            Record<string, EntryFields.RichText | undefined>
          >((sum, curr) => {
            sum[curr.copyItemId] = curr.copyItemValue
            return sum
          }, {})
        })
        return transformedSiteCopyField
      }
      const initLandingPages = async (landingPages: ContentfulPage[]) => {
        if (!contentfulService.isPreviewMode && !landingPages) return []
        const toLoad = landingPages.map((el) => el.sys.id)
        const requestConig = contentfulService.isPreviewMode
          ? {
              skip: 0,
              limit: 999,
              include: 4 as const,
              content_type: 'page',
              'fields.pageType': 'Landing Page',
            }
          : {
              skip: 0,
              limit: toLoad.length,
              include: 4 as const,
              content_type: 'page',
              'sys.id[in]': toLoad,
            }

        const { items } =
          await contentfulService.client.getEntries<ContentfulPageSkeleton>(requestConig)
        return decoratePages(items)
      }
      const initRedirects = (redirectEntries: ContentfulRedirectItem[] = []) => {
        return redirectEntries
          .filter((el) => el.fields)
          .reduce<Record<string, string>>((sum, curr) => {
            sum[curr.fields.from] = curr.fields.to
            return sum
          }, {})
      }

      const initPGPConfigurationFieldsMapping = (
        pgpConfigurationRelationship: ContentfulCompositeCollection<ContentfulProductOptionsRelationshipWrapper> | null,
      ) => {
        const configEntries = pgpConfigurationRelationship?.fields.items || []
        return configEntries
          .filter(
            (el) =>
              el?.fields && el?.sys?.contentType?.sys?.id === 'productOptionsRelationshipWrapper',
          )
          .map((el) => el.fields)
      }

      try {
        contentfulService.service.data.productsMeta = productsMeta
        contentfulService.service.data.slots = slots
        contentfulService.service.data.punchout = punchout
        contentfulService.service.data.catalogs = decoratePages(settingsEntries.catalogs)
        contentfulService.service.data.allPageEntries = decoratePages(allPageEntries)
        contentfulService.service.data.textCopyEntries = initSiteCopyData(settingsEntries)
        contentfulService.service.data.landingPages = await initLandingPages(
          settingsEntries.landingPages,
        )
        contentfulService.service.data.redirects = initRedirects(settingsEntries.redirects)
        contentfulService.service.data.homepagePageMeta = settingsEntries.homepagePageMeta
        contentfulService.service.data.hubSpotGlobalTracker = settingsEntries.hubSpotGlobalTracker
        contentfulService.service.data.microcopy = settingsEntries.microcopy
        contentfulService.service.data.pgpConfigurationRelationship =
          initPGPConfigurationFieldsMapping(settingsEntries.pgpConfigurationRelationship)
        contentfulService.service.data.productDefaultImage = settingsEntries.productDefaultImage

        initEducationalContent(educationalContentItems)
        initRichTextLinkInterceptor()
      } catch (error) {
        const decoratedError = app.config.globalProperties.$err.decorateError('', error as Error)
        app.config.globalProperties.$err.sendError(decoratedError)
        console.error(decoratedError)
      }
    },
  },
}

export default contentfulService
